import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Settings as SettingsIcon, Save, DollarSign, Building, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { CompanySettingsTab } from './components/CompanySettingsTab';
import { CompanyTemplatesUI } from './components/CompanyTemplatesUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function SettingsPage() {
  const [settings, setSettings] = useState({
    // Labor rates
    base_labor_rate: 22.00,
    payroll_burden_pct: 18.0,
    day_porter_hourly_rate: 18.00,
    // Production rates
    production_light: 4500.0,
    production_standard: 3500.0,
    production_heavy: 2500.0,
    production_restroom_intensive: 2000.0,
    // Room time adders (minutes)
    restroom_additional_minutes: 15.0,
    breakroom_additional_minutes: 10.0,
    office_additional_minutes: 3.0,
    conference_room_additional_minutes: 8.0,
    specialty_room_additional_minutes: 8.0,
    // Condition multipliers
    condition_excellent_multiplier: 1.15,
    condition_good_multiplier: 1.0,
    condition_fair_multiplier: 0.85,
    condition_poor_multiplier: 0.70,
    // Access/difficulty multipliers
    multi_floor_elevator_multiplier: 1.05,
    multi_floor_no_elevator_multiplier: 1.15,
    access_easy_multiplier: 1.0,
    access_moderate_multiplier: 1.08,
    access_difficult_multiplier: 1.15,
    trash_low_multiplier: 0.98,
    trash_moderate_multiplier: 1.0,
    trash_high_multiplier: 1.08,
    after_hours_access_multiplier: 1.05,
    // Cost percentages
    overhead_pct: 15.0,
    supply_cost_pct: 5.0,
    tax_pct: 0.0,
    // Markup targets
    markup_recurring: 30.0,
    markup_deep_cleaning: 45.0,
    markup_specialty: 60.0,
    markup_low_offset: 10.0,
    markup_high_offset: 15.0,
    // Frequency discounts
    frequency_discount_5x: 10.0,
    // Minimums
    minimum_monthly_price: 200.00
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api('/api/settings/bidding');
      if (data.values) {
        setSettings(data.values);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api('/api/settings/bidding', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: parseFloat(value) || 0
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          Settings
        </h1>
        <p className="text-muted-foreground">Manage your company and pricing configuration</p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList>
          <TabsTrigger value="company">
            <Building className="h-4 w-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            Proposal Templates
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <DollarSign className="h-4 w-4 mr-2" />
            Pricing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-6">
          <CompanySettingsTab />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <CompanyTemplatesUI />
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <BiddingSettingsTab 
            settings={settings}
            handleChange={handleChange}
            handleSave={handleSave}
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BiddingSettingsTab({ settings, handleChange, handleSave, loading }) {
  return (
    <div className="space-y-6">`
      <div>
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold tracking-tight">
          Pricing Configuration
        </h2>
        <p className="text-muted-foreground">Configure your bidding engine parameters</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Labor Costs */}
        <Card>
          <CardHeader>
            <CardTitle>Labor Costs</CardTitle>
            <CardDescription>Base rates and burden</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="base_labor_rate">Base Hourly Labor Rate ($)</Label>
              <Input
                id="base_labor_rate"
                type="number"
                step="0.01"
                value={settings.base_labor_rate}
                onChange={(e) => handleChange('base_labor_rate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="payroll_burden_pct">Payroll Burden (%)</Label>
              <Input
                id="payroll_burden_pct"
                type="number"
                step="0.1"
                value={settings.payroll_burden_pct}
                onChange={(e) => handleChange('payroll_burden_pct', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Taxes, insurance, benefits</p>
            </div>
            <div>
              <Label htmlFor="day_porter_hourly_rate">Day Porter Rate ($/hr)</Label>
              <Input
                id="day_porter_hourly_rate"
                type="number"
                step="0.01"
                value={settings.day_porter_hourly_rate}
                onChange={(e) => handleChange('day_porter_hourly_rate', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Overhead & Supplies */}
        <Card>
          <CardHeader>
            <CardTitle>Overhead & Supplies</CardTitle>
            <CardDescription>Additional cost factors</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="overhead_pct">Overhead (%)</Label>
              <Input
                id="overhead_pct"
                type="number"
                step="0.1"
                value={settings.overhead_pct}
                onChange={(e) => handleChange('overhead_pct', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="supply_cost_pct">Supply Cost (% when included)</Label>
              <Input
                id="supply_cost_pct"
                type="number"
                step="0.1"
                value={settings.supply_cost_pct}
                onChange={(e) => handleChange('supply_cost_pct', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="tax_pct">Sales Tax (%)</Label>
              <Input
                id="tax_pct"
                type="number"
                step="0.1"
                value={settings.tax_pct}
                onChange={(e) => handleChange('tax_pct', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Production Rates */}
        <Card>
          <CardHeader>
            <CardTitle>Production Rates</CardTitle>
            <CardDescription>Square feet per hour by building type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="production_light">Light Office (sq ft/hr)</Label>
              <Input
                id="production_light"
                type="number"
                step="100"
                value={settings.production_light}
                onChange={(e) => handleChange('production_light', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="production_standard">Standard Office (sq ft/hr)</Label>
              <Input
                id="production_standard"
                type="number"
                step="100"
                value={settings.production_standard}
                onChange={(e) => handleChange('production_standard', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="production_heavy">Heavy Cleaning (sq ft/hr)</Label>
              <Input
                id="production_heavy"
                type="number"
                step="100"
                value={settings.production_heavy}
                onChange={(e) => handleChange('production_heavy', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="production_restroom_intensive">Medical/Restroom-Intensive (sq ft/hr)</Label>
              <Input
                id="production_restroom_intensive"
                type="number"
                step="100"
                value={settings.production_restroom_intensive}
                onChange={(e) => handleChange('production_restroom_intensive', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Markup & Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>Markup & Pricing</CardTitle>
            <CardDescription>Profit margins by service type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="markup_recurring">Recurring Janitorial (%)</Label>
              <Input
                id="markup_recurring"
                type="number"
                step="1"
                value={settings.markup_recurring}
                onChange={(e) => handleChange('markup_recurring', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="markup_deep_cleaning">Deep Cleaning / One-Time (%)</Label>
              <Input
                id="markup_deep_cleaning"
                type="number"
                step="1"
                value={settings.markup_deep_cleaning}
                onChange={(e) => handleChange('markup_deep_cleaning', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="markup_specialty">Specialty / Post-Construction (%)</Label>
              <Input
                id="markup_specialty"
                type="number"
                step="1"
                value={settings.markup_specialty}
                onChange={(e) => handleChange('markup_specialty', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="markup_low_offset">Low Price Offset (-%)</Label>
                <Input
                  id="markup_low_offset"
                  type="number"
                  step="1"
                  value={settings.markup_low_offset}
                  onChange={(e) => handleChange('markup_low_offset', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="markup_high_offset">High Price Offset (+%)</Label>
                <Input
                  id="markup_high_offset"
                  type="number"
                  step="1"
                  value={settings.markup_high_offset}
                  onChange={(e) => handleChange('markup_high_offset', e.target.value)}
                />
              </div>
            </div>
            <Separator />
            <div>
              <Label htmlFor="frequency_discount_5x">Daily Cleaning Discount (%)</Label>
              <Input
                id="frequency_discount_5x"
                type="number"
                step="1"
                value={settings.frequency_discount_5x}
                onChange={(e) => handleChange('frequency_discount_5x', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="minimum_monthly_price">Minimum Monthly Price ($)</Label>
              <Input
                id="minimum_monthly_price"
                type="number"
                step="10"
                value={settings.minimum_monthly_price}
                onChange={(e) => handleChange('minimum_monthly_price', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Room Adjustments */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Room Adjustments</CardTitle>
            <CardDescription>Additional time per room type (minutes)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="restroom_additional_minutes">Restroom (minutes)</Label>
                <Input
                  id="restroom_additional_minutes"
                  type="number"
                  step="1"
                  value={settings.restroom_additional_minutes}
                  onChange={(e) => handleChange('restroom_additional_minutes', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="breakroom_additional_minutes">Breakroom (minutes)</Label>
                <Input
                  id="breakroom_additional_minutes"
                  type="number"
                  step="1"
                  value={settings.breakroom_additional_minutes}
                  onChange={(e) => handleChange('breakroom_additional_minutes', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="office_additional_minutes">Private Office (minutes)</Label>
                <Input
                  id="office_additional_minutes"
                  type="number"
                  step="0.5"
                  value={settings.office_additional_minutes}
                  onChange={(e) => handleChange('office_additional_minutes', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="conference_room_additional_minutes">Conference Room (minutes)</Label>
                <Input
                  id="conference_room_additional_minutes"
                  type="number"
                  step="1"
                  value={settings.conference_room_additional_minutes}
                  onChange={(e) => handleChange('conference_room_additional_minutes', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="specialty_room_additional_minutes">Specialty Room (minutes)</Label>
                <Input
                  id="specialty_room_additional_minutes"
                  type="number"
                  step="1"
                  value={settings.specialty_room_additional_minutes}
                  onChange={(e) => handleChange('specialty_room_additional_minutes', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adjustment Multipliers */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Adjustment Multipliers</CardTitle>
            <CardDescription>Condition, access, and complexity adjustments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3">
                <p className="text-sm font-medium mb-2">Condition Multipliers</p>
              </div>
              <div>
                <Label htmlFor="condition_excellent_multiplier">Excellent</Label>
                <Input
                  id="condition_excellent_multiplier"
                  type="number"
                  step="0.05"
                  value={settings.condition_excellent_multiplier}
                  onChange={(e) => handleChange('condition_excellent_multiplier', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="condition_fair_multiplier">Fair</Label>
                <Input
                  id="condition_fair_multiplier"
                  type="number"
                  step="0.05"
                  value={settings.condition_fair_multiplier}
                  onChange={(e) => handleChange('condition_fair_multiplier', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="condition_poor_multiplier">Poor</Label>
                <Input
                  id="condition_poor_multiplier"
                  type="number"
                  step="0.05"
                  value={settings.condition_poor_multiplier}
                  onChange={(e) => handleChange('condition_poor_multiplier', e.target.value)}
                />
              </div>
              
              <div className="col-span-3 pt-2">
                <Separator />
                <p className="text-sm font-medium mb-2 mt-2">Access Difficulty</p>
              </div>
              <div>
                <Label htmlFor="access_moderate_multiplier">Moderate</Label>
                <Input
                  id="access_moderate_multiplier"
                  type="number"
                  step="0.01"
                  value={settings.access_moderate_multiplier}
                  onChange={(e) => handleChange('access_moderate_multiplier', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="access_difficult_multiplier">Difficult</Label>
                <Input
                  id="access_difficult_multiplier"
                  type="number"
                  step="0.01"
                  value={settings.access_difficult_multiplier}
                  onChange={(e) => handleChange('access_difficult_multiplier', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="after_hours_access_multiplier">After-Hours Issues</Label>
                <Input
                  id="after_hours_access_multiplier"
                  type="number"
                  step="0.01"
                  value={settings.after_hours_access_multiplier}
                  onChange={(e) => handleChange('after_hours_access_multiplier', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading} size="lg" data-testid="save-settings-button">
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : 'Save Pricing Settings'}
        </Button>
      </div>
    </div>
  );
}
